import { useEffect, useState, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import BuzzvilDashboard from '../../../components/pointhome/buzzvil/BuzzvilDashboard';
import BuzzvilDetailMetricsTable from '../../../components/pointhome/buzzvil/BuzzvilDetailMetricsTable';
import apiClient from '../../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import { BuzzvilDetailMetricsData } from '../../../types/buzzvil.types';

export default function Buzzvil() {
  const [buzzvilMetrics, setBuzzvilMetrics] = useState<BuzzvilDetailMetricsData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(0, 'day'));
  const [initialized, setInitialized] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...response.data]);
      });
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const fetchTotalMetrics = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;
    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp 
          ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } 
          : {})
    };

    apiClient.get(`/api/v1/stat/buzzvil/pointhome/detail/metrics`, {
      params: params
    }).then((response) => {
      setBuzzvilMetrics(response.data);
      setErrorMessage('');
    }
    ).catch((error) => {
      setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
    ).finally(() => {
      setLoading(false);
    });
  }, [startDate, endDate, selectedApp]);

  useEffect(() => {
    setInitialized(true);
  }, []);

    useEffect(() => {
        if (initialized && startDate && endDate && selectedApp != null) {
        fetchTotalMetrics();
        }
    }, [fetchTotalMetrics, initialized, startDate, endDate, selectedApp]);

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh'
      }}
    >
      <Box
        alignItems={'center'}
        display={'flex'}
      >
        <Box>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => {
                if (startDate && endDate) {
                    fetchTotalMetrics();
                }
            }}
          />
        </Box>
        <Box
          sx={{
            ml: 3
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(setApp) => {
              setSelectedApp(setApp);
            }}
            onSearch={fetchTotalMetrics}
          />
        </Box>
      </Box>
      
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* 앱 선택 (Chip) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <BuzzvilDashboard
            data={buzzvilMetrics}
          />
        )}
      </Box>

      {/* 테이블 섹션 (로딩 아닐 때만 표시) */}
      {!loading && !errorMessage && (
        <Box
          sx={{
            ...PAGE_CARD_SX,
            width: '55%',
            maxWidth: '55%',
          }}
        >
          {startDate && endDate && (
            <BuzzvilDetailMetricsTable
              data={buzzvilMetrics}
              selectedApp={selectedApp}
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
